/**
 * Debt payoff algorithms: avalanche (highest rate first) vs snowball (lowest balance first).
 * Month-by-month simulation with interest accrual and minimum payments.
 */

const MAX_MONTHS = 360; // 30 years safety cap

export function calculatePayoffTimeline(debts, strategy = 'avalanche', extraMonthly = 0) {
  if (!debts || debts.length === 0) {
    return { debtFreeMonth: 0, totalInterest: 0, timeline: [] };
  }

  // Clone debts as working copies
  let working = debts.map((d) => ({
    id: d.id,
    creditor: d.creditor,
    balance: d.balance || 0,
    rate: (d.rate || 0) / 100, // convert % to decimal
    minPayment: d.minPayment || 0,
  }));

  const timeline = [];
  let totalInterest = 0;
  let month = 0;

  while (working.some((d) => d.balance > 0) && month < MAX_MONTHS) {
    month++;
    let monthInterest = 0;
    const monthData = { month, debts: {} };

    // 1. Accrue interest on all debts
    for (const d of working) {
      if (d.balance <= 0) continue;
      const interest = d.balance * (d.rate / 12);
      d.balance += interest;
      monthInterest += interest;
    }
    totalInterest += monthInterest;

    // 2. Pay minimum on all debts
    let availableExtra = extraMonthly;
    for (const d of working) {
      if (d.balance <= 0) continue;
      const payment = Math.min(d.minPayment, d.balance);
      d.balance -= payment;
    }

    // 3. Apply extra payment to target debt (avalanche/snowball order)
    const activeDebts = working.filter((d) => d.balance > 0);
    if (activeDebts.length > 0 && availableExtra > 0) {
      const sorted = [...activeDebts].sort((a, b) => {
        if (strategy === 'avalanche') return b.rate - a.rate; // highest rate first
        return a.balance - b.balance; // lowest balance first (snowball)
      });

      const target = sorted[0];
      const extraPayment = Math.min(availableExtra, target.balance);
      target.balance -= extraPayment;
    }

    // Record snapshot
    for (const d of working) {
      monthData.debts[d.id] = Math.max(0, d.balance);
    }
    timeline.push(monthData);
  }

  return {
    debtFreeMonth: month,
    totalInterest: Math.round(totalInterest),
    timeline,
  };
}
